import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useQueryParams from "Utils/useQueryParams";
import useSWR from "swr";
import Button from "Components/button/Button";
import { MediaQueriesContext, AuthContext } from "@/App";
import TextInputFilter from "Components/textInputFilter/TextInputFilter";
import t from "Utils/translation";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import PaginationShow from "Components/paginationShow/PaginationShow";
import Pager from "Components/pager/Pager";
import Select from "Components/select/Select";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";
import _ from "lodash";
import cx from "classnames";
import Loader from "Components/loader/Loader";
import { bytesToHuman } from "Utils/bytesFormats";
import CustomerSelect from "Components/customerSelect/CustomerSelect";
import SmallModal from "Components/smallModal/smallModal";
import qs from "query-string";
import {
  Icon,
  refresh,
  arrowDown,
  arrowUp,
  sentMsg,
  hourglass,
  delivered,
  warning,
  warningCircular,
  download,
  disabledEtlive,
  selectLogEtlive,
  mail,
} from "Icons/index";
import SingleDateRangeFilter from "Components/singleDateRangeFilter/SingleDateRangeFilter";
import QboxmailApi from "@/qboxmailApi";

const api = new QboxmailApi({ useCookie: true });

import s from "../delivery.scss";

const FilterCard = ({
  onFilterClick,
  width,
  className,
  label,
  value,
  icon,
  iconBackgroundColor,
  cardBorderColor,
  isFilterActive = false,
}) => (
  <div
    style={width && { width }}
    className={cx(
      `px-2 py-2 cursor-pointer flex items-center rounded  border-solid border-[1px] border-[#EBEBEB] relative overflow-hidden gap-2 border-b-0`,
      className
    )}
    onClick={onFilterClick}
  >
    <div
      className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
        iconBackgroundColor || "bg-[--bg-d3]"
      }`}
    >
      <Icon svg={icon} width="1rem" className="opacity-60" />
    </div>
    <div className="text-sm">{label}</div>

    <div className="flex justify-end flex-grow gap-2 text-lg font-semibold leading-none ">
      {value ? new Intl.NumberFormat(navigator.language).format(value) : "-"}
    </div>

    {/* bottom border */}
    <div
      className={cx("absolute bottom-0 left-0 right-0  border-0 border-b-4 border-solid ", cardBorderColor, {
        "opacity-10": !isFilterActive,
      })}
    ></div>
  </div>
);

const getContentTableLabelTranslation = (label) => {
  return {
    time: t("delivery.tracemail.contentTableItemLabel.time"),
    from: t("delivery.tracemail.contentTableItemLabel.sender"),
    to: t("delivery.tracemail.contentTableItemLabel.recipient"),
    subject: t("delivery.tracemail.contentTableItemLabel.subject"),
    accountId: t("delivery.tracemail.contentTableItemLabel.accountId"),
    username: t("delivery.tracemail.contentTableItemLabel.username"),
    poolType: t("delivery.tracemail.contentTableItemLabel.pool"),
    country: t("delivery.tracemail.contentTableItemLabel.country"),
    smtpId: t("delivery.tracemail.contentTableItemLabel.smtpId"),
    clientIp: t("delivery.tracemail.contentTableItemLabel.senderIp"),
    messageId: t("delivery.tracemail.contentTableItemLabel.messageId"),
    size: t("delivery.tracemail.contentTableItemLabel.size"),
    deliveryDate: t("delivery.tracemail.contentTableItemLabel.deliveryDate"),
    relay: t("delivery.tracemail.contentTableItemLabel.relay"),
    response: t("delivery.tracemail.contentTableItemLabel.response"),
    sessionId: t("delivery.tracemail.contentTableItemLabel.smtpId"),
    mxHost: t("delivery.tracemail.contentTableItemLabel.mxHost"),
    smtp_result: t("delivery.tracemail.contentTableItemLabel.smtpResult"),
  }[label];
};

const getFiltersByCollection = (collection, currentQuery, counters) => {
  if (!collection) {
    return [
      {
        status: undefined,
        icon: mail,
        label: "delivery.tracemail.result.all",
        value: counters?.all,
        iconBackgroundColor: "bg-[--accent-l35]",
        cardBorderColor: "border-qb-blue",
        isFilterActive: !currentQuery.status,
      },
      {
        status: "delivered",
        icon: delivered,
        label: "delivery.tracemail.result.delivered",
        value: counters?.delivered,
        iconBackgroundColor: "bg-qb-green-a20",
        cardBorderColor: "border-qb-green",
        isFilterActive: currentQuery.status === "delivered",
      },
      {
        status: "suppressed",
        icon: warningCircular,
        label: "delivery.tracemail.result.suppressed",
        value: counters?.suppressed,
        iconBackgroundColor: "bg-qb-yellow-a20",
        cardBorderColor: "border-qb-yellow",
        isFilterActive: currentQuery.status === "suppressed",
      },
      {
        status: "bounce",
        icon: warning,
        label: "delivery.tracemail.result.bounced",
        value: counters?.bounce,
        iconBackgroundColor: "bg-qb-red-a20",
        cardBorderColor: "border-qb-red",
        isFilterActive: currentQuery.status === "bounce",
      },
    ];
  }

  if (collection === "queued") {
    return [
      {
        status: undefined,
        icon: sentMsg,
        label: "delivery.tracemail.result.enqueued",
        value: counters?.queued,
        // iconBackgroundColor: "bg-[--bg-d10]",
        cardBorderColor: "border-[--bg-d10]",
        isFilterActive: true,
      },
    ];
  }

  if (collection === "errors") {
    return [
      {
        status: undefined,
        icon: mail,
        label: "delivery.tracemail.result.all",
        value: counters?.errors,
        iconBackgroundColor: "bg-[--red-l35]",
        cardBorderColor: "border-qb-red",
        isFilterActive: !currentQuery.error_category,
      },
      {
        status: "auth",
        icon: delivered,
        label: "delivery.tracemail.result.login",
        value: counters?.authErrors,
        iconBackgroundColor: "bg-[--purple-l35]",
        cardBorderColor: "border-[--purple]",
        isFilterActive: currentQuery.error_category === "auth",
      },
      {
        status: "limits",
        icon: warningCircular,
        label: "delivery.tracemail.result.limits",
        value: counters?.limitsErrors,
        iconBackgroundColor: "bg-[--indigo-l35]",
        cardBorderColor: "border-[--indigo]",
        isFilterActive: currentQuery.error_category === "limits",
      },
    ];
  }

  return [];
};

const ContentTable = ({ className, items, onShowServerResponse }) => (
  <div className={cx("grid grid-cols-[max-content_auto] gap-y-1 gap-x-9", className)}>
    {Object.entries(items).map(([label, value]) => (
      <React.Fragment key={label}>
        <div className="text-sm text-qb-text-a40">{getContentTableLabelTranslation(label) || label}:</div>

        <div className="flex flex-col items-start gap-[1px] text-sm font-semibold justify-self-start">
          {label === "country" ? emojiFlags[value].emoji : value}

          {label === "bounce_message" && (
            <a
              href={`https://documentation.qboxmail.com/bounce-categories`}
              className="text-[--accent] text-sm font-normal"
              onClick={(e) => {
                e.preventDefault();
                if (onShowServerResponse) onShowServerResponse(e);
              }}
            >
              {t("button.showServerResponse")}
            </a>
          )}
        </div>
      </React.Fragment>
    ))}
  </div>
);

const TableDataCell = ({ value, status = null, colSpan = 1 }) => (
  <td className="px-2 border-0 border-b border-solid border-b-[--bg-d5] py-2" colSpan={colSpan}>
    <div className="flex gap-2 text-sm">
      {status && (
        <div
          className={cx("rounded-full w-5 h-5 flex-shrink-0", {
            ["bg-qb-green"]: status === "delivered",
            ["bg-qb-gray"]: status === "enqueued",
            ["bg-qb-yellow"]: status === "suppressed",
            ["bg-qb-red"]: status === "bounce",
          })}
        />
      )}{" "}
      {value}
    </div>
  </td>
);

// eslint-disable-next-line
const ListRow = ({ data, onModalOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMediaMobile } = useContext(MediaQueriesContext);
  const [isExporting, setIsExporting] = useState(false);

  const possibleStatuses = {
    delivered: { icon: delivered, label: t("delivery.tracemail.result.status.delivered"), key: "delivered" },
    bounce: { icon: warning, label: t("delivery.tracemail.result.status.bounced"), key: "bounce" },
    suppressed: { icon: warningCircular, label: t("delivery.tracemail.result.status.suppressed"), key: "suppressed" },
    enqueued: { icon: hourglass, label: t("delivery.tracemail.result.status.enqueued"), key: "enqueued" },
  };

  const statusData = data.to.delivered
    ? possibleStatuses.delivered
    : data.to.bounce
    ? possibleStatuses.bounce
    : data.to.suppressed
    ? possibleStatuses.suppressed
    : possibleStatuses.enqueued;

  const exportPdf = async () => {
    setIsExporting(true);
    const res = await api.exportTracemailDeliveryPDF(data);

    if (res.result === "ok") {
      window.open(BASE_URL_PUBLIC_ASSETS + res.file_path, "_blank");
    } else {
      window.alert(t("etlive.log.exportingError"));
    }

    setIsExporting(false);
  };

  return isOpen ? (
    <tr className="shadow-md">
      <td colSpan={5}>
        <div className="px-2 py-4">
          <div className="flex justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cx("rounded-full w-5 h-5 flex-shrink-0", {
                  ["bg-qb-green"]: statusData.key === "delivered",
                  ["bg-qb-gray"]: statusData.key === "enqueued",
                  ["bg-qb-yellow"]: statusData.key === "suppressed",
                  ["bg-qb-red"]: statusData.key === "bounce",
                })}
              />
              <div className="text-sm font-semibold">
                {t("delivery.tracemail.message")} {statusData.label}
              </div>
            </div>
          </div>
          <div className="ml-7">
            {/* //title */}
            <div className="flex gap-3 mt-6">
              <Icon svg={sentMsg} width="1rem" />
              <div className="text-sm font-semibold">{t("delivery.tracemail.result.status.sent")}</div>
            </div>
            {/* //content */}
            <div>
              {/* send info */}
              <div className="flex flex-col gap-6 md:gap-0 md:flex-row w-full px-5 pb-11 border-0 border-l border-solid border-[--bg-d5] mt-4 ml-2">
                <div className="flex-1">
                  <div className="w-fit">
                    <ContentTable
                      items={{
                        time: `${data.human_date} ${data.human_time}`,
                        from: data.from,
                        to: data.to.recipient,
                        subject: data.subject?.length > 48 ? data.subject.substring(0, 48) + "..." : data.subject,
                        accountId: data.account,
                        username: data.username,
                      }}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="w-fit">
                    <ContentTable
                      items={{
                        poolType: data.poolType,
                        clientIp: data.clientIp,
                        sessionId: data.sessionId,
                        messageId: data.messageId,
                        size: bytesToHuman(data.size),
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* status info */}
              <>
                <div className="flex gap-3 mt-2">
                  <Icon svg={statusData.icon} width="1rem" />
                  <div className="text-sm font-semibold">{statusData.label}</div>
                </div>

                {statusData.key !== "suppressed" && statusData.key !== "enqueued" && (
                  <div className="flex flex-col justify-between gap-6 px-5 mt-4 ml-2 md:flex-row md:gap-0 pb-11">
                    {true && (
                      <div className="flex-1">
                        <div className="w-fit">
                          <ContentTable
                            items={{
                              time: data.to.delivery_date
                                ? `${data.to.human_delivery_date} ${data.to.human_delivery_time}`
                                : null,
                              mxHost: `${data.to.mxHost}[${data.to.mxIp}]`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {data.to.bounce ? (
                      <div className="flex-1">
                        <div className="w-fit">
                          <ContentTable
                            onShowServerResponse={data.to.category ? () => onModalOpen(data.to.smtp_result) : null}
                            items={
                              data.to.category
                                ? {
                                    category: data.to.category || "-",
                                    bounce_message: data.to.bounce_message,
                                  }
                                : {
                                    smtp_result: data.to.smtp_result,
                                  }
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1">
                        <div className="w-fit">
                          <ContentTable
                            items={{
                              smtp_result: data.to.smtp_result,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            </div>
          </div>
        </div>
      </td>
      <td className="align-top">
        <div className="w-fit">
          <ButtonIcon icon={download} onClick={exportPdf} loading={isExporting} />
        </div>
      </td>

      <td className="align-top">
        <div className="w-fit svg-accent">
          <ButtonIcon icon={arrowUp} onClick={() => setIsOpen(false)} />
        </div>
      </td>
    </tr>
  ) : (
    <tr>
      <TableDataCell value={statusData.label} status={statusData.key} />
      <TableDataCell value={data.isToday ? data.human_time : `${data.human_date} ${data.human_time}`} />
      <TableDataCell value={data.from} />
      <TableDataCell value={data.to.recipient} />
      {!isMediaMobile && (
        <TableDataCell value={data.subject?.length > 20 ? data.subject.substring(0, 20) + "..." : data.subject} />
      )}
      <td className="border-0 border-b border-solid border-b-[--bg-d5] py-3">
        <div className="align-top w-fit">
          <ButtonIcon icon={download} onClick={exportPdf} loading={isExporting} />
        </div>
      </td>
      <td className="border-0 border-b border-solid border-b-[--bg-d5]">
        <div className="align-top w-fit svg-accent">
          <ButtonIcon icon={arrowDown} onClick={() => setIsOpen(true)} />
        </div>
      </td>
    </tr>
  );
};

const ListErrorRow = ({ data }) => {
  const { isMediaMobile } = useContext(MediaQueriesContext);
  const [isExporting, setIsExporting] = useState(false);

  let description = "";
  if (data.account_description && data.account_description.length > 30) {
    description = data.account_description.substring(0, 40) + "...";
  } else if (data.account_description) {
    description = data.account_description;
  }

  const accountText = `${data.account} ${description ? `- ${description}` : ""}`;

  return (
    <tr className="border-0 border-b border-solid border-b-[--bg-d5]">
      <td className="px-2 py-2">
        <div className="flex gap-2 text-sm capitalize">
          <div
            className={cx("rounded-full w-5 h-5 flex-shrink-0", {
              ["bg-[--indigo]"]: data.error_category === "limits",
              ["bg-[--purple]"]: data.error_category === "auth",
            })}
          />
          {data.error_category}
        </div>
      </td>

      <TableDataCell value={data.isToday ? data.human_time : `${data.human_date} ${data.human_time}`} />
      <TableDataCell value={accountText} />
      {!isMediaMobile && (
        <td className="px-2 py-2">
          <div className="gap-2 text-sm">
            {data.error_message}
            <span className="text-qb-text-a40"> - {data.error_code}</span>
          </div>
        </td>
      )}
      <td className="flex justify-end">
        <div className="align-top w-fit">
          <ButtonIcon
            icon={download}
            onClick={async () => {
              setIsExporting(true);

              const res = await api.exportTracemailDeliveryPDF(data);

              if (res.result === "ok") {
                window.open(BASE_URL_PUBLIC_ASSETS + res.file_path, "_blank");
              } else {
                window.alert(t("etlive.log.exportingError"));
              }

              setIsExporting(false);
            }}
            loading={isExporting}
          />
        </div>
      </td>
    </tr>
  );
};

const ListControls = ({ totalDeliveredMessages, onRefreshClick, paginationInfo, isLoading }) => {
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search);

  return (
    <div className="flex items-center gap-2">
      <ButtonIcon icon={refresh} onClick={onRefreshClick} />
      <PaginationShow className="w-14" history={history} location={location} />
      <Pager
        loading={isLoading}
        history={history}
        paginationInfo={{ ...query, ...paginationInfo }}
        url={location.pathname}
      />
      {/* <Button onClick={todo}>button.exportCSV</Button> */}
    </div>
  );
};

const Show = () => {
  const { isMediaMobile } = useContext(MediaQueriesContext);
  const { isLoggedUserAdmin } = useContext(AuthContext);

  const queryParams = useQueryParams();
  const parsedQuery = qs.parse(queryParams.toString());

  const [filtersValues, setFiltersValues] = useState({});
  const [areFiltersOpen, setAreFiltersOpen] = useState(true);

  const [resultModalOpened, setResultModalOpened] = useState(false);
  const [resultModalData, setResultModalData] = useState(null);

  const history = useHistory();

  useEffect(() => {
    // set initial filters values

    setFiltersValues(parsedQuery);
  }, []);

  const handleCollectionChange = (query) => {
    let currentQuery = { ...parsedQuery, ...query };

    // reset collection specific filters
    currentQuery.status = undefined;
    currentQuery.error_category = undefined;

    handleQueryChange(currentQuery);
  };

  const handleQueryChange = (query) => {
    let currentQuery = { ...parsedQuery, ...query };

    if (!query.page) currentQuery.page = 1;

    const q = qs.stringify(currentQuery, { addQueryPrefix: true, skipNulls: true, skipEmptyString: true });

    setFiltersValues(currentQuery);

    history.push({
      pathname: location.pathname,
      search: q,
    });
  };

  const composeCountersQuery = (q) => {
    // remove useless keys to avoid useless requests
    let query = { ...q };

    let unwantedKeys = ["page", "per", "status", "collection"];
    for (let key of unwantedKeys) {
      delete query[key];
    }

    return qs.stringify(query, { addQueryPrefix: true, skipNulls: true, skipEmptyString: true });
  };

  const search = () => {
    let query = { ...filtersValues };
    handleQueryChange(query);
  };

  const {
    data: accounts,
    error: accounts_error,
    mutate: mutateAccounts,
  } = useSWR(
    isLoggedUserAdmin()
      ? parsedQuery.customer_code
        ? `/delivery/tracemail/accounts?customer_code=${parsedQuery.customer_code}`
        : null
      : `/delivery/tracemail/accounts`
  );
  const {
    data: domains,
    error: domains_error,
    mutate: mutateDomains,
  } = useSWR(
    isLoggedUserAdmin()
      ? parsedQuery.customer_code
        ? `/delivery/tracemail/domains?customer_code=${parsedQuery.customer_code}`
        : null
      : `/delivery/tracemail/domains`
  );

  const {
    data: mailouts,
    error: mailouts_error,
    isValidating: areMailoutsLoading,
    mutate,
  } = useSWR(parsedQuery.account ? `/delivery/tracemail/mailouts?${queryParams.toString()}` : null, {
    keepPreviousData: true, // necessary for <pager /> component
  });

  const {
    data: counters,
    error: counters_error,
    mutate: mutateCounters,
  } = useSWR(parsedQuery.account ? `/delivery/tracemail/counters?${composeCountersQuery(parsedQuery)}` : null);

  const tracemail_error = accounts_error || counters_error || mailouts_error || domains_error;

  return (
    <div className="flex flex-col h-full px-4 pt-4 max-w-[100vw] overflow-y-scroll">
      <div className="flex">
        <div className="flex-1">
          <div className="text-xl font-semibold">Tracemail</div>
          {!isMediaMobile && <div className="text-sm text-qb-text-a40">{t("delivery.tracemail.desc")}</div>}
        </div>
        <div className="flex justify-end flex-1">
          {/* <Button color="docs" onlyIcon={isMediaMobile}>
            button.docs
          </Button> */}
        </div>
      </div>
      <div className="mt-4 flex gap-4 border-solid border-0 border-b border-[--bg-d5] pb-4 tracemail-filters">
        <div className="flex flex-col w-full gap-4">
          {isLoggedUserAdmin() && (
            <>
              {areFiltersOpen ? (
                <CustomerSelect
                  initialValue={parsedQuery.customer_code}
                  className="w-full"
                  label={t("input.label.customer")}
                  withoutDomainCounter
                  onChange={(data) => {
                    handleQueryChange({ customer_code: data?.value });

                    if (!data) {
                      handleQueryChange({ account: null, domain: null });
                      mutateAccounts(undefined, { revalidate: false, populateCache: false });
                      mutateDomains(undefined, { revalidate: false, populateCache: false });
                    }
                  }}
                />
              ) : (
                <div className="flex items-center gap-1 text-sm">
                  <div className="text-[--text-a60]">{t("input.label.customer")}:</div>
                  <div>{parsedQuery.customer_code}</div>
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {areFiltersOpen ? (
              <Select
                isClearable
                label={t("delivery.label.account")}
                placeholder={t("input.placeholder.account.select")}
                components={{ DropdownIndicator }}
                name="account"
                disabled={!accounts}
                options={accounts?.resources.map((account) => ({
                  value: account.id,
                  label: account.description,
                }))}
                value={parsedQuery.account}
                onChange={handleQueryChange}
                optionComponent={(props) => {
                  // console.log("props", props.data.value, props.isFocused, props.isSelected);

                  return (
                    <div
                      {...props.innerProps}
                      className={cx("flex flex-col px-2 py-1 hover:bg-gray-50 hover:cursor-pointer", {
                        "bg-gray-50": props.isFocused,
                      })}
                    >
                      <div className="text-sm font-semibold ">{props.data.value}</div>
                      <div
                        className={cx("text-xs opacity-70", {
                          "text-red-500": props.data.deleted_at,
                        })}
                      >
                        {props.data.label || "Cancellato"}
                      </div>
                    </div>
                  );
                }}
              />
            ) : (
              <div className="flex items-center gap-1 text-sm">
                <div className="text-[--text-a60]">{t("delivery.label.account")}:</div> <div>{parsedQuery.account}</div>
              </div>
            )}

            {areFiltersOpen ? (
              <>
                <Select
                  isClearable
                  label={t("input.label.domain")}
                  placeholder={t("input.placeholder.domain.select")}
                  components={{ DropdownIndicator }}
                  name="domain"
                  disabled={!domains || !parsedQuery.account}
                  options={domains?.resources.map((domain) => ({
                    value: domain.name,
                    label: domain.name,
                  }))}
                  value={parsedQuery.domain}
                  onChange={handleQueryChange}
                  isActive={!!parsedQuery.domain}
                />

                <TextInputFilter
                  name="from"
                  label={t("input.label.sender")}
                  placeholder={t("input.placeholder.delivery.tracemail.sender")}
                  onChange={(value) => setFiltersValues({ ...filtersValues, from: value })}
                  value={filtersValues.from}
                  isActive={parsedQuery.from && parsedQuery.from === filtersValues.from}
                  onSubmit={handleQueryChange}
                  disabled={!parsedQuery.account}
                />
              </>
            ) : (
              <>
                <div className="flex items-center gap-1 text-sm">
                  <div className="text-[--text-a60]">{t("input.label.domain")}:</div>{" "}
                  <div>{parsedQuery.domain || "-"}</div>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <div className="text-[--text-a60]">{t("input.label.sender")}:</div>{" "}
                  <div>{parsedQuery.from || "-"}</div>
                </div>
              </>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {areFiltersOpen ? (
              <>
                <SingleDateRangeFilter
                  onChange={([startDate, endDate]) => {
                    const edits = {
                      start_date: startDate || undefined,
                      end_date: endDate || undefined,
                    };

                    handleQueryChange(edits);
                  }}
                  isActive={parsedQuery.start_date || parsedQuery.end_date}
                  disabled={!parsedQuery.account}
                  startDate={parsedQuery.start_date}
                  endDate={parsedQuery.end_date}
                  label={t("input.label.delivery.tracemail.dateRange")}
                  className="w-full"
                />
                <TextInputFilter
                  name="to"
                  label={t("input.label.recipient")}
                  placeholder={t("input.placeholder.delivery.tracemail.recipient")}
                  onChange={(value) => setFiltersValues({ ...filtersValues, to: value })}
                  value={filtersValues.to}
                  isActive={parsedQuery.to && parsedQuery.to === filtersValues.to}
                  onSubmit={handleQueryChange}
                  disabled={!parsedQuery.account}
                />
                <TextInputFilter
                  name="subject"
                  label={t("input.label.message.subject")}
                  placeholder={t("input.placeholder.delivery.tracemail.messageSubject")}
                  onChange={(value) => setFiltersValues({ ...filtersValues, subject: value })}
                  value={filtersValues.subject}
                  isActive={parsedQuery.subject && parsedQuery.subject === filtersValues.subject}
                  onSubmit={handleQueryChange}
                  disabled={!parsedQuery.account}
                />
              </>
            ) : (
              <>
                <div className="flex items-center gap-1 text-sm">
                  <div className="text-[--text-a60]">{t("input.label.delivery.tracemail.dateRange")}:</div>{" "}
                  <div>
                    {parsedQuery.start_date && parsedQuery.end_date
                      ? `${parsedQuery.start_date} - ${parsedQuery.end_date}`
                      : "-"}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <div className="text-[--text-a60]">{t("input.label.recipient")}:</div>{" "}
                  <div>{parsedQuery.to || "-"}</div>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <div className="text-[--text-a60]">{t("input.label.message.subject")}:</div>{" "}
                  <div>{parsedQuery.subject || "-"}</div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className={cx("flex flex-col items-center self-end justify-between h-full", { "pt-7": areFiltersOpen })}>
          <div
            className="text-sm text-[--accent] flex items-center gap-1 cursor-pointer hover:bg-[--accent-l35] px-1 py-[1px] rounded-sm"
            onClick={() => setAreFiltersOpen(!areFiltersOpen)}
          >
            <Icon
              svg={areFiltersOpen ? arrowUp : arrowDown}
              width=".7rem"
              height=".7rem"
              className="icon-stroke-accent"
            />
            {areFiltersOpen ? t("button.hide") : t("button.show")}
          </div>
          <Button onClick={search} color="outline" className={{ invisible: !areFiltersOpen }}>
            button.search
          </Button>
        </div>
      </div>

      {parsedQuery.account && (
        <>
          <div className="flex mt-6 mb-4 justify-between w-full border-b-[1px] border-solid border-0 border-[var(--bg-d5)] pb-1">
            {/* tabs */}
            <div className="flex items-end relative bottom-[-5px] ">
              {[
                {
                  label: `${t("table.header.processed")} ${
                    counters?.all === undefined
                      ? ""
                      : `[${new Intl.NumberFormat(navigator.language).format(counters.all || 0)}]`
                  }`,
                  query: { collection: undefined },
                },

                {
                  label: `${t("delivery.accounts.dashboard.activities.enqueued")} ${
                    counters?.queued === undefined
                      ? ""
                      : `[${new Intl.NumberFormat(navigator.language).format(counters.queued || 0)}]`
                  }`,
                  query: { collection: "queued" },
                },

                {
                  label: `${t("table.header.errors")} ${
                    counters?.errors === undefined
                      ? ""
                      : `[${new Intl.NumberFormat(navigator.language).format(counters.errors || 0)}]`
                  }`,
                  query: { collection: "errors" },
                },
              ].map((q) => {
                return (
                  <div
                    className={cx(
                      "h-8 text-sm flex items-center px-2 whitespace-nowrap cursor-pointer border-0 border-solid",
                      {
                        ["text-[--accent] border-b-[2px] !border-[--accent]"]:
                          parsedQuery.status === "error"
                            ? q.query.status === "error"
                            : parsedQuery.collection === q.query.collection && q.query.status !== "error",
                      }
                    )}
                    onClick={() => handleCollectionChange(q.query)}
                  >
                    {q.label}
                  </div>
                );
              })}
            </div>

            {/* <div className="flex items-end relative bottom-[-2px] z-10">
              <div
                className={cx(
                  "h-8 text-sm flex items-center px-2 whitespace-nowrap cursor-pointer border-b border-0 border-solid border-[--bg-d5]",
                  {
                    ["text-[--accent] border-b-[2px] !border-[--accent]"]: parsedQuery.collection !== "queued",
                  }
                )}
                onClick={() => handleQueryChange({ collection: undefined })}
              >
                {t("table.header.processed")}
              </div>
              <div
                className={cx(s.itemTab, { [s.active]: parsedQuery.collection === "queued" })}
                onClick={() =>
                  handleQueryChange({
                    collection: "queued",
                  })
                }
              >
                {t("delivery.accounts.dashboard.activities.enqueued")} ({counters?.queued || 0})
              </div>
            </div> */}

            {/* pagination controls */}
            <ListControls
              totalDeliveredMessages={counters ? counters.all + counters.queued : null}
              onRefreshClick={() => {
                mutate();
                mutateCounters();
              }}
              paginationInfo={mailouts?.pagination}
              isLoading={areMailoutsLoading}
            />
          </div>
        </>
      )}

      {parsedQuery.account && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 max-w-fit">
          {getFiltersByCollection(parsedQuery.collection, parsedQuery, counters).map(
            ({ status, icon, label, value, iconBackgroundColor, cardBorderColor, isFilterActive }) => (
              <FilterCard
                key={status || "all"}
                onFilterClick={() =>
                  handleQueryChange(parsedQuery.collection === "errors" ? { error_category: status } : { status })
                }
                icon={icon}
                width="200px"
                label={t(label)}
                value={value}
                iconBackgroundColor={iconBackgroundColor}
                cardBorderColor={cardBorderColor}
                isFilterActive={isFilterActive}
              />
            )
          )}
        </div>
      )}

      {areMailoutsLoading && (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader iconSize="3rem" />
        </div>
      )}

      {tracemail_error && (
        <div className="flex flex-col items-center justify-center py-8">
          <Icon svg={disabledEtlive} width="10rem" height="10rem" />
          <div className="text-xl font-medium">{t("infoMsg.etliveUnavailable.msg")}</div>
        </div>
      )}

      {parsedQuery.account && !tracemail_error && !areMailoutsLoading && mailouts?.resources.length > 0 && (
        <div className="flex flex-col flex-1 overflow-x-scroll overflow-y-scroll ">
          <table className="w-full mt-5 border-collapse ">
            <thead className="bg-[#f8f8f8] h-8">
              {parsedQuery.collection === "errors" ? (
                <tr className="border-b border-solid border-0 border-b-[--bg-d5] bg-[--bg]">
                  <th className="px-2 text-sm font-semibold text-left w-[100px]">{t("table.header.error_category")}</th>
                  <th className="px-2 text-sm font-semibold text-left w-[180px]">{t("table.header.time")}</th>
                  <th className="w-[25%] px-2 text-sm font-semibold text-left">{t("table.header.account")}</th>
                  {!isMediaMobile && (
                    <th className="px-2 text-sm font-semibold text-left">{t("table.header.error")}</th>
                  )}
                  <th className="w-[5%]"></th>
                </tr>
              ) : (
                <tr>
                  <th className="px-2 text-sm font-semibold text-left w-28">{t("table.header.status")}</th>
                  <th className="px-2 text-sm font-semibold text-left w-28">{t("table.header.time")}</th>
                  <th className="w-1/4 px-2 text-sm font-semibold text-left">
                    {t("delivery.tracemail.contentTableItemLabel.sender")}
                  </th>
                  <th className="w-1/4 px-2 text-sm font-semibold text-left">
                    {t("delivery.tracemail.contentTableItemLabel.recipient")}
                  </th>
                  {!isMediaMobile && (
                    <th className="w-1/4 px-2 text-sm font-semibold text-left">
                      {t("delivery.tracemail.contentTableItemLabel.subject")}
                    </th>
                  )}
                  <th className="w-5"></th>
                  <th className="w-5"></th>
                </tr>
              )}
            </thead>
            <tbody>
              {_.compact(
                mailouts?.resources.map((el, idx) => {
                  if (el.data.error) {
                    return <ListErrorRow data={el.data} key={el.data.messageId + idx} />;
                  }

                  return (
                    <ListRow
                      onModalOpen={(message) => {
                        setResultModalOpened(true);
                        setResultModalData(message);
                      }}
                      data={el.data}
                      key={el.data.messageId + idx}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!areMailoutsLoading && mailouts?.resources.length === 0 && parsedQuery.account && (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="text-xl font-medium">{t("infoMsg.noRecords.msg")}</div>
        </div>
      )}

      {!parsedQuery.account && (
        <div className="flex flex-col items-center justify-center py-20">
          <Icon svg={selectLogEtlive} width="10rem" height="10rem" />
          <div className="text-xl font-medium">{t("infoMsg.delivery.selectAccountFirst")}</div>
        </div>
      )}

      <SmallModal
        closeModal={() => {
          setResultModalOpened(false);
          setResultModalData(null);
        }}
        title={t("etlive.log.title.email.not.received")}
        opened={resultModalOpened}
        showCloseIcon
      >
        <div className="px-6">
          <pre className="p-1 text-sm whitespace-pre-wrap bg-gray-100 section">{resultModalData}</pre>
        </div>
        <div className="mb-4">
          <Button
            className="self-end"
            type="button"
            onClick={() => {
              setResultModalOpened(false);
              setResultModalData(null);
            }}
          >
            button.close
          </Button>
        </div>
      </SmallModal>
    </div>
  );
};

export default Show;
